import { useLayoutEffect, useRef } from 'react';
import { EASE_OUT, prefersReducedMotion } from '@/lib/motion';

// Each row fades in from 4px above over ROW_MS, the next one STEP_MS after
// it; past MAX_STEPS rows the rest start together, so a long list settles
// within ROW_MS + MAX_STEPS * STEP_MS (300ms) like a short one.
const ROW_MS = 180;
const STEP_MS = 12;
const MAX_STEPS = 10;

// The value list's rows, in document order: the rows of each <dl> (the
// company ID row, the preview's rows, a group's rows) and the group headers.
const ROWS = ':scope > dl > *, :scope > div > button, :scope > div > dl > *';
const isValueRow = (element) => element.parentElement?.tagName === 'DL';

/**
 * Sweeps a passport's value rows in, top to bottom, when they load: the
 * panel's first paint, another passport (`key` changes), "Show all" or a
 * group opened. Seen often, so it is short and slight.
 *
 * Only rows that were not on screen before sweep. Rows that merely moved,
 * such as the preview's first rows reappearing at the top of the full list,
 * are new elements to React but not to the reader, so as many rows as just
 * left the list are taken as replaced and keep still. While `paused` (the
 * filter or the Missing toggle at work) rows come and go without motion: a
 * list that answers each keystroke must not move with it.
 */
export default function useRowSweep(listRef, { key, enabled = true, paused = false }) {
    const known = useRef([]);
    const lastKey = useRef(key);

    useLayoutEffect(() => {
        const list = listRef.current;
        if (!enabled || !list) return;

        let replaced = 0;
        if (lastKey.current !== key) {
            lastKey.current = key;
            known.current = [];
        } else {
            replaced = known.current.filter((element) => !element.isConnected && isValueRow(element)).length;
        }

        const current = new Set(known.current);
        const rows = [...list.querySelectorAll(ROWS)];
        const fresh = rows.filter((element) => !current.has(element));
        known.current = rows;
        // No Web Animations API (jsdom, an old engine): the rows just appear.
        if (paused || fresh.length === 0 || typeof list.animate !== 'function' || prefersReducedMotion()) return;

        let skippedRows = 0;
        let step = 0;
        for (const element of fresh) {
            if (skippedRows < replaced) {
                if (isValueRow(element)) skippedRows += 1;
                continue;
            }
            element.animate(
                [{ opacity: 0, transform: 'translateY(-4px)' }, { opacity: 1, transform: 'none' }],
                { duration: ROW_MS, delay: Math.min(step, MAX_STEPS) * STEP_MS, easing: EASE_OUT, fill: 'backwards' },
            );
            step += 1;
        }
    });
}
